import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import partition from 'lodash/partition';
import { Tab, Tabs } from '@pwc/appkit-react/lib/Tabs';

import RolesGrid from './rolesGrid.component';
import RolesPrivilegesGrid from './rolesPrivilegesGrid.component';

import Spinner from '../../shared/spinner.component';
import NavigationPrompt from '../../shared/navigationPrompt.component';
import ConfirmDialog from '../../shared/confirmDialog.component';
import Button from '../../shared/button.component';
import { permissions, userRoles, withPermissions } from '../../shared/authorization';
import { useAddRole, useDeleteRole, useUpdateRole } from '../../shared/mutations/roles';
import { currentPeriodSelector } from '../../shared/store/selectors';
import { useRoleData, useRoles } from '../../shared/queries/roles';
import { GridMode, useEditableGridLogic } from '../../shared/gridEdit/useEditableGridLogic';
import { useClientsForUser } from '../../shared/queries/client';

import { filterByPrivileges, getPermissionsByRoleAndClientSelector } from '../../userSettings/addEditUserSettingsForm.helpers';
import {
  availablePermissionsForRoleSelector,
  clientsFormDataSelector,
  filteredSettingsFormDataSelector,
  normalizedLoggedInUserAssignmentsSelector,
  settingsFormDataSelector,
} from '../../userSettings/store/selectors';

import {
  availablePermissionsPropTypes,
  loggedInUserAssignmentsPropTypes,
  userSettingsFormDataPropTypes,
  userToEditPropTypes,
} from '../../userSettings/propTypes';

import { fetchInitialDataForUserSettingsPage, fetchLoggedInUserSettings } from '../../userSettings/store/actions';

import { validateRole } from './validators';
import {
  createRoleRow,
  getClientsSetWithEditPermissionsForUser,
  parseRoles,
  selectFirstRow,
} from './helpers';
import CompanyAssignments, { PillTypes } from '../../userSettings/companyAssignments/companyAssignments.component';
import { filterAssignmentsByType } from '../../utils/permissionsHelpers';
import { assignmentTypes } from '../../shared/authorization/assignmentTypes';

const TabTypes = {
  USER_PRIVILEGES: 'USER_PRIVILEGES',
  COMPANY_ASSIGNMENTS_AND_PRIVILEGES: 'COMPANY_ASSIGNMENTS_AND_PRIVILEGES',
};


const useTabs = initialTab => {
  const [activeTab, setActiveTab] = useState(initialTab);

  const onChangeTab = (_, activeTab) => setActiveTab(activeTab);

  return {
    activeTab,
    onChangeTab,
  };
};

const prop = prop => obj => obj?.[prop];
const propEq = (prop, value) => obj => obj?.[prop] === value;

const RolesPage = ({
  loggedInUserSso,
  availablePermissions,
  fetchInitialDataForUserSettingsPage,
  isFetchingInitialDataForUserSettings,
  currentPeriod,
  canModify,
  fetchLoggedInUserSettings,
  clientsDataForUser,
  loggedInUser,
  loggedInUserAssignments,
  userRole,
  userSettingsFormData,
}) => {
  const [modifiedRoleId, setModifiedRoleId] = useState(0);
  const [currentUserPrivileges, setCurrentUserPrivileges] = useState([]);
  const [checkedAssignments, setCheckedAssignments] = useState([]);
  const [roleModified, setRoleModified] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);
  const [mode, setMode] = useState(GridMode.View);
  const { activeTab, onChangeTab } = useTabs(TabTypes.USER_PRIVILEGES);

  const [activeCompanyAssignmentsPill, setActiveCompanyAssignmentsPill] = useState(PillTypes.CURRENT_ASSIGNMENTS);
  const changeCompanyAssignmentsPill = (pillType, callback) => {
    setActiveCompanyAssignmentsPill(pillType);
    callback?.();
  };

  const { mutate: addRole, isLoading: isAdding } = useAddRole(() => setMode(GridMode.View));
  const { mutate: updateRole, isLoading: isUpdating } = useUpdateRole(() => setMode(GridMode.View));
  const { mutate: deleteRole, isLoading: isDeleting } = useDeleteRole();
  const { data: rawRoles, isLoading: areRolesLoading } = useRoles();
  const { data: { clients: allUserClients }, isLoading: areClientsLoading } = useClientsForUser();

  const {
    isFetching: isLoadingRoleData,
  } = useRoleData({ roleId: selectedRow?.roleId }, {
    onSuccess: ({ roleAssignments = [] }) => {
      const [
        userAssignments,
        nonUserAssignments,
      ] = partition(roleAssignments, propEq('assignmentType', assignmentTypes.USER));

      setCurrentUserPrivileges(userAssignments?.[0]?.privileges?.map(prop('privilegeCode')) ?? []);
      setCheckedAssignments(nonUserAssignments.map(assignment => ({
        ...assignment,
        privileges: assignment.privileges.map(prop('privilegeCode')),
      })));
    },
  });

  const clients = useMemo(() => {
    if (!allUserClients?.length || !loggedInUser?.clients.length) {
      return [];
    }
    const clientsWithPermissions = getClientsSetWithEditPermissionsForUser(loggedInUser);
    return allUserClients.filter(({ clientId }) => clientsWithPermissions.has(clientId));
  }, [allUserClients, loggedInUser]);
  const roles = useMemo(() => parseRoles(rawRoles, clients), [rawRoles, clients]);
  const selectedClient = useMemo(
    () => clients?.find(({ clientId }) => clientId === selectedRow?.clientId),
    [selectedRow, clients]);
  const roleForSelectedClient = useMemo(
    () => clientsDataForUser.find(({ clientId }) => clientId === selectedClient?.clientId),
    [clientsDataForUser, selectedClient?.clientId]
  );

  const isClientSelected = useCallback(
    ({ clientId }) => selectedClient?.clientId === clientId,
    [selectedClient]
  );

  const shouldShowAllCheckbox = userRole === userRoles.SUPER_USER
    || userRole === userRoles.POWER_USER
    || loggedInUserAssignments
      .filter(isClientSelected)
      .some(filterAssignmentsByType(assignmentTypes.ALL));

  const userSettingsFormDataFilteredByClient = filteredSettingsFormDataSelector(
    selectedClient ? selectedClient.clientId : null,
    true,
    userSettingsFormData
  );


  const checkAssignment = useCallback((value, checked, type) => {
    setRoleModified(true);
    setCheckedAssignments(checkedAssignments => checked
      ? checkedAssignments.concat({
        assignmentCode: value,
        assignmentType: type,
        privileges: [],
      })
      : checkedAssignments.filter(({ assignmentCode, assignmentType }) =>
        assignmentCode !== value
        || assignmentType !== type
      )
    );
  }, []);

  const checkAllAssignments = useCallback((values, checked, type) => {
    setRoleModified(true);
    setCheckedAssignments(checkedAssignments => checked
      ? checkedAssignments.concat(values.map(value => ({
        assignmentCode: value,
        assignmentType: type,
        privileges: [],
      })))
      : checkedAssignments.filter(({ assignmentType }) => assignmentType !== type));
  }, []);

  const changeAssignmentsPermissions = useCallback(({
    selectedAssignments,
    assignmentTypeCode,
    newValues,
    removedValues,
  }) => {
    setRoleModified(true);

    setCheckedAssignments(previousCheckedAssignments => {
      const getNewValue = previousValue => {
        const newValue = previousValue.slice();

        selectedAssignments.forEach(selectedAssignment => {
          const assignmentToUpdate = newValue.find(({ assignmentCode, assignmentType }) => (
            assignmentCode === selectedAssignment
            && assignmentType === assignmentTypeCode
          ));

          if (assignmentToUpdate) {
            assignmentToUpdate.privileges = assignmentToUpdate.privileges
              .filter(value => !removedValues.includes(value))
              .concat(newValues.filter(newValue => !assignmentToUpdate.privileges.includes(newValue)));
          } else {
            newValue.push({
              assignmentCode: selectedAssignment,
              assignmentType: assignmentTypeCode,
              privileges: newValues,
            });
          }
        });

        return newValue;
      };

      return getNewValue(previousCheckedAssignments);
    });
  }, []);


  const isRowEditable = selectedRow?.allowEdit === 'Y';
  const readOnly = mode === GridMode.View;
  const isLoading = isAdding
    || isLoadingRoleData
    || isDeleting
    || isUpdating
    || isFetchingInitialDataForUserSettings
    || !availablePermissions.length
    || areClientsLoading;

  const userSettingsFormDataFilteredByPrivileges = readOnly
  || userRole === userRoles.SUPER_USER
  || userRole === userRoles.POWER_USER
    ? userSettingsFormDataFilteredByClient
    : filterByPrivileges(
      userSettingsFormDataFilteredByClient,
      loggedInUserAssignments,
      isClientSelected
    );

  const updateSelected = useCallback(gridApi => {
    const newSelectedRow = gridApi?.getSelectedRows?.()?.[0];
    const rowsAreDifferent = !isEqual(newSelectedRow, selectedRow);
    const isEditModeCase = mode === GridMode.Edit && newSelectedRow?.roleId === modifiedRoleId;
    const isAddModeCase = mode === GridMode.Add && newSelectedRow?.roleId === createRoleRow().roleId;
    if (rowsAreDifferent
      && (readOnly
        || isEditModeCase
        || isAddModeCase)
    ) {
      setSelectedRow(newSelectedRow);
      if (mode === GridMode.View) {
        setCurrentUserPrivileges(newSelectedRow?.privilegeList || []);
      }
    }
  }, [mode, modifiedRoleId, readOnly, selectedRow]);

  useEffect(() => {
    if (!availablePermissions.length && !isFetchingInitialDataForUserSettings) {
      fetchInitialDataForUserSettingsPage(isFetchingInitialDataForUserSettings, currentPeriod);
      fetchLoggedInUserSettings({ sso: loggedInUserSso });
    }
  }, [
    fetchLoggedInUserSettings,
    fetchInitialDataForUserSettingsPage,
    currentPeriod,
    isFetchingInitialDataForUserSettings,
    availablePermissions.length,
    loggedInUserSso,
  ]);

  const onCancel = useCallback((hasChanges, reset) => {
    setMode(GridMode.View);
    reset();
  },
  [setMode]);

  const onSave = useCallback(async ({
    updatedRows,
    addedRows,
    hasErrors,
  }) => {
    if (hasErrors) {
      return;
    }
    const updatedRowSource = updatedRows.length ? updatedRows : roles;
    const roleToSave = {
      ...[
        ...addedRows,
        ...updatedRowSource,
      ].find(({ roleId }) => roleId === modifiedRoleId),
      roleAssignments: [
        {
          assignmentType: assignmentTypes.USER,
          assignmentCode: 'SSO',
          privileges: currentUserPrivileges.map(privilegeCode => ({ privilegeCode })),
        },
        ...checkedAssignments.map(assignment => ({
          ...assignment,
          privileges: assignment.privileges.map(privilegeCode => ({ privilegeCode })),
        })),
      ],
    };

    setRoleModified(false);
    switch (mode) {
      case GridMode.Edit:
        await updateRole(roleToSave);
        break;
      case GridMode.Add:
        await addRole(roleToSave);
        break;
      default:
        break;
    }
  }, [addRole, currentUserPrivileges, mode, modifiedRoleId, roles, updateRole, checkedAssignments]);

  const {
    onGridReady,
    save,
    cancel,
    setRowData: editableGridSetRowData,
    addRow,
    getRowNodeId,
    onSelectionChanged,
    setEditedRow,
    gridApi,
  } = useEditableGridLogic({
    createRow: createRoleRow,
    data: roles,
    getRowUniqueId: ({ roleId }) => roleId,
    mode,
    validateRow: validateRole,
    onSave,
    onCancel,
    onSelect: updateSelected,
    context: { clients },
    includeMetaWhenSaving: true,
  });

  const setRowData = useCallback(props => {
    updateSelected(gridApi);
    return editableGridSetRowData(props);
  }, [editableGridSetRowData, gridApi, updateSelected]);

  const editRole = useCallback(editedRoleId => {
    setMode(GridMode.Edit);
    setModifiedRoleId(editedRoleId);
  }, []);

  const onEditRuleSettingsClicked = useCallback(() => {
    if (!selectedRow) {
      selectFirstRow(gridApi);
    }
    editRole(selectedRow?.roleId);
  }, [editRole, gridApi, selectedRow]);

  const onRowSelectedToEdit = useCallback(data => {
    editRole(data.roleId);
    setEditedRow(data);
  }, [editRole, setEditedRow]);

  const onRowSelectedToDelete = useCallback(roleId => {
    deleteRole(roleId);
  }, [deleteRole]);

  const onPermissionsChange = useCallback(permissions =>
    setCurrentUserPrivileges(permissions),
  [setCurrentUserPrivileges]);

  const onAddRole = useCallback(() => {
    setMode(GridMode.Add);
    setModifiedRoleId(0);
    setCurrentUserPrivileges([]);
    setCheckedAssignments([]);
    addRow();
  }, [addRow]);

  const componentToRenderOnSearchBar = mode === GridMode.View && (
    <Button
      className="a-my-10"
      kind="primary"
      type="button"
      disabled={mode !== GridMode.View}
      onClick={onAddRole}
    >
      Add Role
    </Button>
  );

  updateSelected(gridApi);
  return (
    <div className="roles-page">
      <div className="page-without-secondary-menu">
        <div className="container-fluid">
          <div className="row no-gutters page-content-container">
            <div className="col">
              <div className="d-flex flex-row justify-content-between">
                <h5>Roles</h5>
              </div>
            </div>
          </div>
          <div className="row no-gutters">
            <div className="col">
              <RolesGrid
                loggedInUserSso={loggedInUserSso}
                onGridReady={onGridReady}
                componentToRenderOnSearchBar={componentToRenderOnSearchBar}
                isGridLoading={isLoading}
                addingNewRole={mode === GridMode.Add}
                isRoleModified={false}
                editedRoleId={modifiedRoleId}
                onRowSelectedToEdit={onRowSelectedToEdit}
                onRowSelectedToDelete={onRowSelectedToDelete}
                areRolesLoading={areRolesLoading}
                onRowSelected={onSelectionChanged}
                canModify={canModify}
                readOnly={readOnly}
                getRowId={getRowNodeId}
                context={{
                  mode,
                  editedRowId: modifiedRoleId,
                }}
                setRowData={setRowData}
                clients={clients}
              />
            </div>
          </div>
          {
            selectedRow && isLoadingRoleData && (
              <div className="row no-gutters">
                <div className="col">
                  <div className="d-flex justify-content-center">
                    <Spinner/>
                  </div>
                </div>
              </div>
            )
          }
          {
            selectedRow && !isLoadingRoleData
            && (
              <div>
                <div className="row no-gutters">
                  <div className="col">
                    {!isLoading && (
                      <Tabs
                        defaultValue={TabTypes.USER_PRIVILEGES}
                        value={activeTab}
                        onChange={onChangeTab}
                        className="a-tab-bar-margin-10"
                        size="md"
                      >
                        <Tab value={TabTypes.USER_PRIVILEGES} label="User Privileges">
                          <RolesPrivilegesGrid
                            getPermissionsFunction={getPermissionsByRoleAndClientSelector}
                            changePermissions={onPermissionsChange}
                            selectedPermissionsFormValue={currentUserPrivileges || []}
                            availablePermissions={availablePermissions}
                            shouldResetValues={false}
                            selectedClient={selectedClient}
                            selectedRole={roleForSelectedClient}
                            readOnly={readOnly}
                            checkedAssignments={checkedAssignments}
                            changeAssignmentsPermissions={changeAssignmentsPermissions}
                            availableAssignmentPermissions={userSettingsFormData.availableAssignmentPermissions}
                          />
                        </Tab>
                        <Tab
                          value={TabTypes.COMPANY_ASSIGNMENTS_AND_PRIVILEGES}
                          label="Company Assignments and Privileges"
                        >
                          <CompanyAssignments
                            userSettingsFormData={userSettingsFormDataFilteredByPrivileges}
                            checkAssignment={checkAssignment}
                            checkAllAssignments={checkAllAssignments}
                            loggedInUserCheckedAssignments={loggedInUserAssignments}
                            checkedAssignments={checkedAssignments}
                            changeCompanyAssignmentsPill={changeCompanyAssignmentsPill} // eslint-disable-line react/jsx-no-bind
                            activeCompanyAssignmentsPill={activeCompanyAssignmentsPill}
                            changeAssignmentsPermissions={changeAssignmentsPermissions}
                            isVisible={activeTab === TabTypes.COMPANY_ASSIGNMENTS_AND_PRIVILEGES}
                            readOnly={readOnly
                            || !selectedClient
                            }
                            userRole={userRole}
                            showAllCheckbox={shouldShowAllCheckbox}
                            elementToRenderBeforePills={
                              <div className="row">
                                <div className="col-12 col-md-2 mb-4">
                                  <div className="user-client-label">
                                    {`Client: ${selectedClient?.name}`}
                                  </div>
                                </div>
                              </div>
                            }
                            selectedClient={selectedClient}
                            isSelectedClientChecked
                            availablePermissions={availablePermissions}
                            selectedRole={roleForSelectedClient}
                            selectedPermissionsFormValue={currentUserPrivileges || []}
                          />
                        </Tab>

                      </Tabs>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-row-reverse mt-2">
                  {mode !== GridMode.View
                  && (
                    <Button
                      type="button"
                      kind="primary"
                      disabled={false}
                      onClick={save}
                      isLoading={isAdding || isUpdating}
                    >
                      Save
                    </Button>
                  )}
                  {readOnly && isRowEditable
                  && (
                    <Button
                      type="button"
                      kind="primary"
                      disabled={false}
                      onClick={onEditRuleSettingsClicked}
                    >
                      Edit Role Settings
                    </Button>
                  )}
                  {mode !== GridMode.View
                  && (
                    <Button
                      type="button"
                      kind="secondary"
                      className="a-mx-10"
                      onClick={cancel}
                      disabled={isAdding || isUpdating}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>)
          }
        </div>
      </div>
      <NavigationPrompt show={roleModified}/>
      <ConfirmDialog
        onConfirm={null}
        onCancel={null}
        isVisible={false}
      />
    </div>
  );
};

RolesPage.propTypes = {
  loggedInUserSso: PropTypes.string.isRequired,
  loggedInUser: userToEditPropTypes,
  loggedInUserAssignments: loggedInUserAssignmentsPropTypes,
  userSettingsFormData: userSettingsFormDataPropTypes.isRequired,
  userRole: PropTypes.string.isRequired,
  availablePermissions: availablePermissionsPropTypes.isRequired,
  fetchInitialDataForUserSettingsPage: PropTypes.func.isRequired,
  fetchLoggedInUserSettings: PropTypes.func.isRequired,
  isFetchingInitialDataForUserSettings: PropTypes.bool.isRequired,
  currentPeriod: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  canModify: PropTypes.bool.isRequired,
  clientsDataForUser: PropTypes.arrayOf(PropTypes.shape({
    clientId: PropTypes.number.isRequired,
  })),
};

const mapStateToProps = state => ({
  loggedInUserAssignments: normalizedLoggedInUserAssignmentsSelector(state),
  loggedInUserSso: state.user.user.sso,
  loggedInUser: state.users.loggedInUser,
  userRole: state.user.user.role,
  userSettingsFormData: settingsFormDataSelector(state),
  clientsDataForUser: clientsFormDataSelector(state),
  availablePermissions: availablePermissionsForRoleSelector(state),
  isFetchingInitialDataForUserSettings: state.users.isFetchingInitialDataForUserSettings,
  currentPeriod: currentPeriodSelector(state),
});

export default withPermissions(permissions.USER_ADMINISTRATION)(connect(
  mapStateToProps,
  {
    fetchInitialDataForUserSettingsPage,
    fetchLoggedInUserSettings,
  }
)(RolesPage));
